import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { Card } from '@sorare/core/src/components/card/Card';
import { ANY_SPORT_CARDS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { DumbNotification } from 'components/activity/DumbNotification';

import { commonNotificationInterfaceFragment } from '../fragments';
import type { CommonNotificationProps } from '../types';
import type { SaleNotification_saleNotification } from './__generated__/index.graphql';

type Props = CommonNotificationProps & {
  notification: SaleNotification_saleNotification;
};

export const SaleNotification = ({ notification, ...rest }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();

  const { name, createdAt, anyCard: card, sport, read } = notification;

  if (!card) return null;

  const link = generateSportContextPath(ANY_SPORT_CARDS_SLUG, {
    params: {
      slug: card.slug,
    },
    sport: card.sport,
  });

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={notification.markdownBody} />}
      userAvatar={currentUser}
      sport={sport}
      cardPicture={<Card card={card} />}
      createdAt={createdAt}
      link={link}
      read={read}
      {...rest}
    />
  );
};

SaleNotification.fragments = {
  saleNotification: gql`
    fragment SaleNotification_saleNotification on SaleNotification {
      id
      ...Notification_notificationInterface
      sport
      anyCard {
        slug
        sport
        ...Card_anyCard
        player: anyPlayer {
          slug
          displayName
        }
      }
    }
    ${commonNotificationInterfaceFragment}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<SaleNotification_saleNotification>,
};
